import type { CalendarEventDto } from '@chat-emp/shared';

import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { LoadingState } from '../components/LoadingState.js';
import { addDays, startOfDay } from './calendar-rules.js';
import { EventCard } from './EventCard.js';
import styles from './EventPanel.module.css';

interface EventPanelProps {
  events: CalendarEventDto[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  /** The event the details panel is showing, so the list can mark its row. */
  selectedId: string | null;
  onOpen: (event: CalendarEventDto) => void;
}

/**
 * The Event column — what the employer has set up to happen at a time.
 *
 * "Event", not "Interview": an interview is the first kind and the reason we are
 * building this, but the section is meant to hold whatever else gets scheduled
 * here later. Naming it for today's single case would mean renaming the route,
 * the folder and the label the first time a second kind arrives.
 *
 * Grouped by how near it is, not by date. A header per calendar day is a header
 * for every row once the diary is more than a week deep, and it buries the only
 * question this column exists to answer — what is next — under whichever Monday
 * happens to sort first.
 */
export function EventPanel({
  events,
  loading,
  failed,
  onRetry,
  selectedId,
  onOpen,
}: EventPanelProps) {
  const bands = bandsOf(events);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h1 className={styles.title}>Event</h1>
      </div>
      <div className={styles.list}>
        {loading ? (
          <LoadingState label="Looking up your schedule" />
        ) : failed ? (
          <EmptyState
            icon={<Icon name="calendar" size={32} />}
            title="Couldn't load your events"
            description="Something went wrong reaching the server."
            action={{ label: 'Try again', onClick: onRetry }}
          />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<Icon name="calendar" size={32} />}
            title="Event"
            description="Any event you set will show up here."
          />
        ) : (
          bands.map((band) => (
            <section key={band.label} className={styles.group}>
              <p className={band.label === 'Today' ? styles.dayToday : styles.day}>
                <span>{band.label}</span>
                <span className={styles.dayCount}>{band.events.length}</span>
              </p>
              {band.events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  selected={event.id === selectedId}
                  // Today and Tomorrow name the day, so those rows spend their
                  // right edge on the hour instead; the other two do not, so
                  // those rows carry the date.
                  showDate={band.dated}
                  onOpen={() => onOpen(event)}
                />
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
}

interface Band {
  label: string;
  events: CalendarEventDto[];
  /** Whether its rows have to say which day they are on. */
  dated: boolean;
}

/**
 * The four bands, empty ones dropped.
 *
 * "Finished" last and newest-first, which is the one place in this column where
 * time runs backwards: what just happened is what gets written up, and what
 * happened three weeks ago is nearly always scroll.
 */
function bandsOf(events: CalendarEventDto[]): Band[] {
  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  const later = addDays(today, 2);

  const byStart = (a: CalendarEventDto, b: CalendarEventDto) =>
    Date.parse(a.startsAt) - Date.parse(b.startsAt);

  const finished: CalendarEventDto[] = [];
  const todays: CalendarEventDto[] = [];
  const tomorrows: CalendarEventDto[] = [];
  const upcoming: CalendarEventDto[] = [];

  for (const event of events) {
    const start = new Date(event.startsAt);
    // By the day it starts, not by whether it has ended: an interview that ran
    // at 09:00 is still one of today's, and it says so by fading rather than by
    // moving to a band the employer has to go looking in.
    if (start < today) finished.push(event);
    else if (start < tomorrow) todays.push(event);
    else if (start < later) tomorrows.push(event);
    else upcoming.push(event);
  }

  return [
    { label: 'Today', events: todays.sort(byStart), dated: false },
    { label: 'Tomorrow', events: tomorrows.sort(byStart), dated: false },
    { label: 'Upcoming', events: upcoming.sort(byStart), dated: true },
    { label: 'Finished', events: finished.sort((a, b) => byStart(b, a)), dated: true },
  ].filter((band) => band.events.length > 0);
}
